import { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { Outlet } from 'react-router-dom';
import Header from './Header/Header';
import Footer from './Footer/Footer';
import MuiXLicense from './MaterialProLicense';
import FilterBox from './FilterBox/FilterBox';
import { useIsUserManagement } from '../hooks/useIsUserManagement.ts';
import StatusBar from './StatusBar/StatusBar.tsx';
import KeepAliveModal from './Modals/KeepAliveModal/KeepAliveModal';
import { useAuth } from '../providers/AuthProvider';
import { useIdleTimer } from '../hooks/useIdleTimer';
import { useKeepAliveMutation } from '../api/auth.api';

const IDLE_THRESHOLD_MS = 15 * 60_000;
const WARNING_WINDOW_MS = 2 * 60_000;

export default function Layout()
{
	const isUserManagement = useIsUserManagement();
	const { isAuthenticated, logout } = useAuth();
	const [keepAlive] = useKeepAliveMutation();

	const [warningOpen, setWarningOpen] = useState(false);
	const [remainingMs, setRemainingMs] = useState(WARNING_WINDOW_MS);

	const handleTimeout = useCallback(async () =>
	{
		setWarningOpen(false);
		await logout();
	}, [logout]);

	const handleWarn = useCallback(() =>
	{
		setRemainingMs(WARNING_WINDOW_MS);
		setWarningOpen(true);
	}, []);

	const { reset } = useIdleTimer({
		idleThresholdMs: IDLE_THRESHOLD_MS,
		warningWindowMs: WARNING_WINDOW_MS,
		onWarn: handleWarn,
		onTimeout: handleTimeout,
		enabled: isAuthenticated,
	});

	// Countdown tick while the warning modal is visible.
	useEffect(() =>
	{
		if (!warningOpen)
		{
			return;
		}
		const intervalId = setInterval(() =>
		{
			setRemainingMs((prev) => Math.max(0, prev - 1000));
		}, 1000);
		return () => clearInterval(intervalId);
	}, [warningOpen]);

	const handleStay = useCallback(async () =>
	{
		// Fail-safe: if the server rejects keep-alive, we must NOT tell the client
		// its session was extended — that leads to a 401 on the next action and
		// silent data loss. Force logout instead so client + server stay in sync.
		try
		{
			await keepAlive().unwrap();
			setWarningOpen(false);
			reset();
		}
		catch (err)
		{
			console.warn('keep-alive failed; logging out to keep client and server in sync', err);
			setWarningOpen(false);
			await logout();
		}
	}, [keepAlive, reset, logout]);

	const handleLeave = useCallback(async () =>
	{
		setWarningOpen(false);
		await logout();
	}, [logout]);

	return (
		<Box
			sx={(theme) => ({
				backgroundColor: theme.palette.background.paper,
				display: 'flex',
				flexDirection: 'column',
				minHeight: '100vh',
				width: '100%',
				pt: 0,
				mt: 0,
			})}
		>
			{/* Material UI X Pro License -- do not remove */}
			<MuiXLicense />
			<Header />
			<StatusBar />
			{/* Main Content Area */}
			<Box component="main" sx={{ flexGrow: 1 }}>
				{/* Content container with 80% width */}
				<Box
					sx={{
						backgroundColor: 'background.paper',
						borderRadius: 1,
						maxWidth: '1600px',
						width: { xs: '100%', sm: '80%', md: '90%' },
						p: '2rem 1.5rem',
						m: '2rem auto',
					}}
				>
					{/* Overview Filters */}
					{!isUserManagement && <FilterBox sx={{ mb: 2 }} />}
					<Outlet />
				</Box>
			</Box>
			<Footer />
			<KeepAliveModal
				open={warningOpen}
				remainingMs={remainingMs}
				totalMs={WARNING_WINDOW_MS}
				onStay={handleStay}
				onLeave={handleLeave}
			/>
		</Box>
	);
}
